import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { type ControlActivityAction, ControlActivityEdit } from '@client/components/controls/control-activity-edit';
import { isWhereClauseEmpty } from '@client/components/filters/core/where-builder';
import { ICONS } from '@client/constants/icons';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import type { RouterInputs } from '@client/trpc/client';
import { DEEP_LIST_OFFSET_LIMIT, DEFAULT_PAGE_SIZE } from '@shared/constants';
import { FilterOperatorSchema } from '@shared/schemas/filter';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Checkbox } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { useState } from 'react';
import { z } from 'zod';
import { ControlActivityFilters } from './ControlActivityFilters';
import { useControlActivityColumns } from './columns';

const ControlActivitySearchSchema = z.object({
    'control-activity-filters': z
        .array(
            z.union([
                z.object({
                    id: z.string(),
                    filterId: z.literal('owner'),
                    operator: FilterOperatorSchema,
                    value: z.union([z.array(z.coerce.bigint()), z.coerce.bigint()]),
                }),
                z.object({
                    id: z.string(),
                    filterId: z.string(),
                    operator: FilterOperatorSchema,
                    value: z.any(),
                }),
            ]),
        )
        .optional()
        .catch(undefined),
});

export const Route = createFileRoute('/console/$orgId/controls/activities')({
    staticData: {
        ui: {
            title: 'Control activities',
            subtitle: 'View and manage control activities and their progress',
            icon: ICONS.controlActivity,
        },
        breadcrumb: {
            title: 'Control activities',
        },
    },
    validateSearch: zodValidator(ControlActivitySearchSchema),
    component: ControlActivitySearch,
});

type ControlActivityWhere = NonNullable<RouterInputs['control']['activity']['listControlActivities']['where']>;

function ControlActivitySearch() {
    const { trpc } = useTrpc();
    const searchParams = Route.useSearch();
    const [showCompleted, setShowCompleted] = useState(false);
    const [controlActivityAction, setControlActivityAction] = useState<ControlActivityAction>(['none']);
    const [filterBarWhere, setFilterBarWhere] = useState<ControlActivityWhere>({});

    // Default: show only uncompleted activities (rating: null)
    const [queryParams, setQueryParams] = usePathState<RouterInputs['control']['activity']['listControlActivities']>(
        'queryParams',
        {
            limit: DEFAULT_PAGE_SIZE,
            orderBy: [{ dueDate: 'asc' }, { createdAt: 'desc' }],
            where: {
                OR: [{ rating: null }],
            },
        },
    );

    const { data, isPending, refetch } = trpc.control.activity.listControlActivities.useQuery(queryParams);

    const columns = useControlActivityColumns(showCompleted);

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isPending,
        columns,
        rowKey: 'id',
        queryParams: queryParams,
        onParamsChange: setQueryParams,
        pagination: { position: 'both', maxAccessibleRecords: DEEP_LIST_OFFSET_LIMIT },
        // The hidden open-items default in `where` is not a user filter; only the filter bar is.
        emptyState: {
            noRecordsTitle: showCompleted ? 'No control activities yet' : 'No open control activities',
            hasActiveFilters: !isWhereClauseEmpty(filterBarWhere),
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'control-activities-table-columns',
            enableReordering: true,
        },
        refreshable: { enabled: true, onRefresh: () => refetch() },
    });

    const handleCheckboxChange = (e: CheckboxChangeEvent) => {
        const checked = e.target.checked;
        setShowCompleted(checked);

        // Build where clause: AND all filter bar conditions with the rating filter
        const ratingFilter = {
            OR: [{ rating: null }, ...(checked ? [{ rating: { not: null } }] : [])],
        } satisfies ControlActivityWhere;

        const whereConditions: ControlActivityWhere[] = [];
        if (Object.keys(filterBarWhere).length > 0) {
            whereConditions.push(filterBarWhere);
        }
        whereConditions.push(ratingFilter);

        setQueryParams((prev) => ({
            ...prev,
            where: whereConditions.length === 1 ? whereConditions[0] : { AND: whereConditions },
            offset: 0,
        }));
    };

    return (
        <PageController route={Route}>
            <ControlActivityEdit
                action={controlActivityAction}
                onClose={() => {
                    setControlActivityAction(['none']);
                    refetch();
                }}
            />

            <TableToolbar
                filters={
                    <ControlActivityFilters
                        initialFilters={searchParams['control-activity-filters']}
                        showCompleted={showCompleted}
                        onChange={(where) => {
                            setFilterBarWhere(where);

                            const ratingFilter: ControlActivityWhere = {
                                OR: [{ rating: null }, ...(showCompleted ? [{ rating: { not: null } }] : [])],
                            };

                            const whereConditions: ControlActivityWhere[] = [];
                            if (Object.keys(where).length > 0) {
                                whereConditions.push(where);
                            }
                            whereConditions.push(ratingFilter);

                            setQueryParams((prev) => ({
                                ...prev,
                                where: whereConditions.length === 1 ? whereConditions[0] : { AND: whereConditions },
                                offset: 0,
                            }));
                        }}
                    />
                }
                actions={
                    <Checkbox checked={showCompleted} onChange={handleCheckboxChange}>
                        Show completed activities
                    </Checkbox>
                }
            />

            <PrismaTable
                {...tableProps}
                scroll={{ x: 'max-content' }}
                onRow={({ id, controlId }) => ({
                    className: 'cursor-pointer',
                    onClick: () => {
                        setControlActivityAction(['edit', id, controlId]);
                    },
                })}
            />
        </PageController>
    );
}
